import { memoize } from 'lodash-es'
import qs, { type defaultDecoder } from 'qs'
import * as React from 'react'
import { useNavigate, useSearchParams } from 'react-router'

export function useSearchStateMutate(name: string) {
	return useLazySearchStateMutate()(name)
}

export function useLazySearchStateMutate() {
	const navigate = useNavigate()

	return (name: string) => (args: any) => {
		const [searchString] = window.location.hash.match(/\?(.*)/) ?? ['']
		const search = qs.parse(searchString, {
			ignoreQueryPrefix: true,
			arrayLimit: Number.MAX_SAFE_INTEGER,
			throwOnLimitExceeded: true,
		})

		if (!args) {
			const { [name]: _, ...omitted } = (search || {}) as any

			navigate({ search: qs.stringify(omitted) })
			return
		}

		const newSearch = qs.stringify({
			...search,
			[name]: { ...(search?.[name] as any), ...args },
		})

		navigate({ search: newSearch })
	}
}

export function useSearchState(name: string) {
	const [searchParams, setSearchParams] = useSearchParams()

	const value = React.useMemo(() => {
		const search = parseSearch(searchParams.toString())

		const setArgs = (args: any) => {
			if (!args) {
				const { [name]: _, ...omitted } = (search || {}) as any

				setSearchParams(qs.stringify(omitted, {}))
				return
			}

			const newSearch = qs.stringify({
				...search,
				[name]: { ...(search?.[name] as any), ...args },
			})

			// setArgs(null)

			setSearchParams(newSearch)
		}

		return {
			open: Boolean(search?.[name]),
			onClose: () => {
				requestAnimationFrame(() => {
					setArgs(null)
				})
			},
			getArgs<TParams = unknown>() {
				return (search?.[name] || null) as TParams | null
			},
			setArgs,
		}
	}, [name, searchParams, setSearchParams])

	return value
}

const parseSearch = memoize((search) => {
	if (!search) {
		return null
	}

	const result = qs.parse(search, {
		arrayLimit: Number.MAX_SAFE_INTEGER,
		throwOnLimitExceeded: true,
		decoder: (
			str: string,
			defaultDecoder: defaultDecoder,
			charset: string,
			_type: 'key' | 'value',
		) => defaultDecoder(str, null, charset),
	})

	return result
})
